// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconProps } from "@insite/mobius/Icon";
import LazyImage from "@insite/mobius/LazyImage";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { screen } from "@stoneage/XDL/Helpers";
import { color, styledSlider, xScreen } from "@stoneage/XDL/Utilities";
import parse from "html-react-parser";
import debounce from "lodash/debounce";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

// eslint-disable-next-line
import { A11y, Navigation, Pagination, Scrollbar } from "swiper";
// eslint-disable-next-line
import { Swiper, SwiperSlide } from "swiper/react";

const enum fields {
    testimonials = "testimonials",
}

const enum testimonialFields {
    title = "title",
    copy = "copy",
    author = "author",
    imageUrl = "imageUrl",
}

interface TestimonialModel {
    fields: {
        title: string;
        copy: string;
        author: string;
        imageUrl: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.testimonials]: TestimonialModel[];
    };
}

type Props = OwnProps;

export interface TestimonialsStyles {
    container?: InjectableCss;
    sliderContainer?: InjectableCss;
    authorElement?: InjectableCss;
    imageElement?: InjectableCss;
    iconElement?: IconProps;
    buttonPrevIcon?: IconProps;
    buttonNextIcon?: IconProps;
}

const MOBILE_CONTAINER_CLASS = "mobile-container";

export const testimonialsStyles: TestimonialsStyles = {
    container: {
        css: css`
            > div {
                display: flex;
                justify-content: center;
            }
        `,
    },
    sliderContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            max-width: 858px !important;
            text-align: center;
            align-items: center;
        `,
    },
    authorElement: {
        css: css`
            width: 100%;
            box-sizing: border-box;
            border-top: 1px solid ${color("common.ui6")};
            padding-top: 19px;
        `,
    },
    imageElement: {
        css: css`
            padding-top: 12px;
        `,
    },
    buttonPrevIcon: {
        src: "ChevronLeft",
        size: 30,
        color: "primary",
    },
    buttonNextIcon: {
        src: "ChevronRight",
        size: 30,
        color: "primary",
    },
};

const styles = testimonialsStyles;

export const testimonialsContainer = css`
    position: relative;
    ${styledSlider()}
    .swiper-container {
        width: 100% !important;
        padding: 15px 0;
        margin: 0 15px;
    }

    .swiper-slide {
        display: flex;
        justify-content: center;
        padding: 5px 0;
    }

    && .swiper-button-next-testimonial {
        transform: translateY(-50%);
        right: 72px;
        height: 56px;
        width: 56px;
        border-radius: 50%;
        background-color: ${color("common.white")};
        ${xScreen("gt", "md")} {
            &:hover,
            &:focus {
                background-color: ${color("primary.main")};
                span svg {
                    color: ${color("common.white")};
                }
            }
        }
    }

    && .swiper-button-prev-testimonial {
        transform: translateY(-50%);
        left: 72px;
        height: 56px;
        width: 56px;
        border-radius: 50%;
        background-color: ${color("common.white")};
        ${xScreen("gt", "md")} {
            &:hover,
            &:focus {
                background-color: ${color("primary.main")};
                span svg {
                    color: ${color("common.white")};
                }
            }
        }
    }

    .swiper-pagination {
        display: none;
    }

    &&& .${MOBILE_CONTAINER_CLASS} {
        &&,
        .swiper-button-prev-testimonial {
            position: absolute;
            top: 100%;
            left: 37%;
            background-color: inherit;
            svg {
                color: ${color("common.ui7")};
                height: 19px;
            }
            ${xScreen("lte", "md")} {
                left: 41%;
            }
            ${xScreen("lte", "sm")} {
                left: 33%;
            }
        }

        &&,
        .swiper-button-next-testimonial {
            position: absolute;
            top: 100%;
            right: 37%;
            background-color: inherit;
            svg {
                color: ${color("common.ui7")};
                height: 19px;
            }
            ${xScreen("lte", "md")} {
                right: 41%;
            }
            ${xScreen("lte", "sm")} {
                right: 33%;
            }
        }
        &&,
        .swiper-pagination {
            display: block;
            .swiper-pagination-bullet {
                margin-right: 0 !important;
                height: 8px;
                width: 8px;
                border: 1px solid ${color("common.ui6")};
            }
        }
        &&,
        .swiper-pagination-bullet-active {
            background: ${color("common.ui7")};
        }
        &&,
        .swiper-pagination-bullets-dynamic {
            width: 49px !important;
            bottom: 19px;
        }
        &&,
        .x-slide-container {
            padding-bottom: 32px;
        }
    }
`;

const Testimonials = ({ id, fields: { testimonials } }: Props) => {
    const [showSpinner, setShowSpinner] = useState(true);
    const [appliedContainerClass, setAppliedContainerClass] = useState("");
    useEffect(() => {
        const getStyles = async () => {
            // eslint-disable-next-line global-require
            const result = await require("swiper/swiper-bundle.min.css");
        };
        getStyles().then(result => {
            setShowSpinner(false);
        });
    }, []);

    const handleContainerStyles = debounce(() => {
        const elementWidth = document.querySelector(`.testimonials-container-${id}`)?.getBoundingClientRect().width;
        if (!elementWidth) {
            return;
        }

        if (elementWidth <= 1024 || window?.innerWidth <= 1024) {
            setAppliedContainerClass(MOBILE_CONTAINER_CLASS);
        } else {
            setAppliedContainerClass("");
        }
    }, 100);

    useEffect(() => {
        window.addEventListener("resize", handleContainerStyles);
        handleContainerStyles();
        return () => {
            window.removeEventListener("resize", handleContainerStyles);
        };
    }, []);

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper className={`testimonials-container-${id}`} css={testimonialsContainer}>
                <Swiper
                    spaceBetween={250}
                    slidesPerView={1}
                    className={`swiper-carousel ${appliedContainerClass}`}
                    modules={[Navigation, Scrollbar, Pagination, A11y]}
                    wrapperClass="swiper-container"
                    navigation={{
                        nextEl: `.swiper-button-next.swiper-button-next-testimonial.id-${id}`,
                        prevEl: `.swiper-button-prev.swiper-button-prev-testimonial.id-${id}`,
                    }}
                    loop={true}
                    pagination={{
                        clickable: true,
                        dynamicBullets: true,
                        dynamicMainBullets: 1000,
                        renderBullet(index, className) {
                            return `<span class="${className}"></span>`;
                        },
                    }}
                >
                    {testimonials.map(({ fields }, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <SwiperSlide key={index}>
                            <StyledWrapper {...styles.sliderContainer} className="x-slide-container">
                                <StyledWrapper
                                    css={css`
                                        margin-bottom: calc(32px - 1rem);
                                    `}
                                >
                                    <Typography>{parse(fields.title, parserOptions)}</Typography>
                                </StyledWrapper>
                                <Typography>{parse(fields.copy, parserOptions)}</Typography>
                                {fields.author && (
                                    <StyledWrapper {...styles.authorElement}>
                                        <Typography>{parse(fields.author, parserOptions)}</Typography>
                                    </StyledWrapper>
                                )}
                                {fields.imageUrl && (
                                    <StyledWrapper {...styles.imageElement}>
                                        <LazyImage
                                            src={fields.imageUrl}
                                            width="140"
                                            height="60"
                                            altText={`${parse(fields.title, parserOptions)}`}
                                        />
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </SwiperSlide>
                    ))}
                </Swiper>
                <StyledWrapper className={`swiper-navigation ${appliedContainerClass}`}>
                    <StyledWrapper className={`swiper-button-prev swiper-button-prev-testimonial id-${id}`}>
                        <Icon {...styles.buttonPrevIcon} />
                    </StyledWrapper>
                    <StyledWrapper className={`swiper-button-next swiper-button-next-testimonial id-${id}`}>
                        <Icon {...styles.buttonNextIcon} />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: Testimonials,
    definition: {
        group: "XNGAGE" as any,
        icon: "list-ul",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.testimonials,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return `${item.fields?.title}`;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: testimonialFields.title,
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },
                    {
                        name: testimonialFields.copy,
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },
                    {
                        name: testimonialFields.author,
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },
                    {
                        name: testimonialFields.imageUrl,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
